import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, RefreshCw, Ticket, FileText, Mail, Sparkles, Copy, Check, ChevronUp } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  LoadingState,
  Modal,
  PageHeader,
  PriorityBadge,
  StatusBadge,
} from '@/components/ui'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import { type CachedDocument } from '@/api/sapDocuments.ts'

// ─── Types ──────────────────────────────────────────────────────────────────

type TodoPriority = 'critical' | 'high' | 'medium' | 'low'
type TodoSource = 'tanss' | 'sap-order' | 'sap-quote' | 'email'

interface TodoMeta {
  sapDocEntry?: number
  sapTenantId?: string
  sapDocNum?: number
  tanssId?: string
  emailWebLink?: string
  emailMessageId?: string
}

interface TodoItem {
  id: string
  source: TodoSource
  priority: TodoPriority
  title: string
  subtitle: string
  dueDate: string | null
  ageInDays: number
  deepLink: string | null
  meta: TodoMeta
}

interface AggregatedTodosResult {
  items: TodoItem[]
  sources: {
    tanss: { ok: boolean; error?: string }
    sap: { ok: boolean; error?: string; perTenantErrors?: Record<string, string> }
    email: { ok: boolean; error?: string }
  }
}

// ─── Source labels / badge mappings ─────────────────────────────────────────

const SOURCE_LABELS: Record<TodoSource, string> = {
  tanss: 'TANSS',
  'sap-order': 'SAP Auftrag',
  'sap-quote': 'SAP Angebot',
  email: 'E-Mail',
}

type SourceFilter = 'all' | TodoSource

function formatRelativeTime(minutes: number): string {
  if (minutes < 60) return `vor ${Math.round(minutes)} Min.`
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return m > 0 ? `vor ${h} Std. ${m} Min.` : `vor ${h} Std.`
}

// ─── SAP-Modal Stub ──────────────────────────────────────────────────────────

function buildSapDocStub(item: TodoItem): CachedDocument {
  return {
    id: item.id,
    tenantId: item.meta.sapTenantId ?? '',
    docType: item.source === 'sap-order' ? 'order' : 'quotation',
    sapDocEntry: item.meta.sapDocEntry ?? 0,
    sapDocNum: item.meta.sapDocNum ?? null,
    cardCode: null,
    cardName: item.title.replace(/^(Auftrag|Angebot) /, ''),
    docDate: item.dueDate,
    docDueDate: null,
    docTotal: null,
    docCurrency: null,
    docStatus: 'bost_Open',
    isClosed: false,
    isCancelled: false,
    comments: null,
    linesJson: null,
    fullDataJson: null,
    lastSyncedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// ─── Email Draft Modal ───────────────────────────────────────────────────────

interface EmailDraftModalProps {
  item: TodoItem
  onClose: () => void
}

function EmailDraftModal({ item, onClose }: EmailDraftModalProps) {
  const messageId = item.meta.emailMessageId
  const [draft, setDraft] = useState('')
  const [copied, setCopied] = useState(false)

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ draft: string }>('/todos/ai/draft-email-reply', {
        messageId,
      })
      return res.data.draft
    },
    onSuccess: (text) => setDraft(text),
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/email/${messageId}/reply`, { body: draft })
    },
    onSuccess: onClose,
  })

  useEffect(() => {
    if (messageId) generateMutation.mutate()
  }, [messageId]) // eslint-disable-line react-hooks/exhaustive-deps

  function copyToClipboard() {
    navigator.clipboard.writeText(draft).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Antwort entwerfen — ${item.title}`}
      size="lg"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={copyToClipboard} disabled={!draft}>
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => sendMutation.mutate()}
              disabled={!draft || sendMutation.isPending}
              loading={sendMutation.isPending}
            >
              <Mail className="w-4 h-4 mr-1" />
              Senden
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {generateMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="w-4 h-4 animate-pulse text-brand-500" />
            KI generiert Entwurf…
          </div>
        )}
        {generateMutation.isError && (
          <div className="text-sm text-destructive">
            Fehler beim Generieren: {(generateMutation.error as Error).message}
          </div>
        )}
        {sendMutation.isError && (
          <div className="text-sm text-destructive">
            Fehler beim Senden: {(sendMutation.error as Error).message}
          </div>
        )}
        <textarea
          className="w-full min-h-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={generateMutation.isPending ? 'Wird generiert…' : 'Entwurf bearbeiten…'}
          disabled={generateMutation.isPending}
        />
        <p className="text-xs text-muted-foreground">
          Bitte prüfen und bei Bedarf anpassen, bevor Sie senden.
        </p>
      </div>
    </Modal>
  )
}

// ─── Ticket Comment Suggestion (Inline Expand) ───────────────────────────────

interface TicketCommentSuggestionProps {
  tanssId: string
  onClose: () => void
}

function TicketCommentSuggestion({ tanssId, onClose }: TicketCommentSuggestionProps) {
  const [copied, setCopied] = useState(false)

  const query = useQuery({
    queryKey: ['ai-ticket-suggestion', tanssId],
    queryFn: async () => {
      const res = await apiClient.post<{ suggestion: string }>('/todos/ai/suggest-ticket-comment', {
        ticketId: tanssId,
      })
      return res.data.suggestion
    },
    staleTime: Infinity, // don't re-fetch once generated
  })

  function copyToClipboard() {
    if (!query.data) return
    navigator.clipboard.writeText(query.data).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="mt-2 ml-2 rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Sparkles className="w-3.5 h-3.5 text-brand-500" />
        KI-Kommentarvorschlag
      </div>
      {query.isPending && (
        <p className="text-muted-foreground animate-pulse">Wird generiert…</p>
      )}
      {query.isError && (
        <p className="text-destructive">
          Fehler: {(query.error as Error).message}
        </p>
      )}
      {query.data && (
        <>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{query.data}</p>
          <div className="flex justify-between items-center">
            <Button variant="ghost" size="xs" onClick={copyToClipboard}>
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
            <Button variant="ghost" size="xs" onClick={onClose}>
              Schließen
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function MyDayPage() {
  const queryClient = useQueryClient()
  // Bug 2: read active tenant selection to forward to the aggregator
  const activeTenantIds = useTenantStore(s => s.activeTenantIds)
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [sapModalDoc, setSapModalDoc] = useState<CachedDocument | null>(null)
  const [emailDraftItem, setEmailDraftItem] = useState<TodoItem | null>(null)
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null)
  const [elapsedMinutes, setElapsedMinutes] = useState<number>(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, isError, error, isFetching, dataUpdatedAt } = useQuery<AggregatedTodosResult>({
    // Bug 2: include activeTenantIds in queryKey so cache invalidates on tenant change
    queryKey: ['todos-aggregated', activeTenantIds],
    queryFn: async () => {
      const tenantParam = activeTenantIds.length > 0 ? `?tenantIds=${activeTenantIds.join(',')}` : ''
      const res = await apiClient.get<AggregatedTodosResult>(`/todos/aggregated${tenantParam}`)
      return res.data
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  // Track "zuletzt aktualisiert"
  useEffect(() => {
    if (dataUpdatedAt) {
      setLastFetchedAt(dataUpdatedAt)
      setElapsedMinutes(0)
    }
  }, [dataUpdatedAt])

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (!lastFetchedAt) return

    timerRef.current = setInterval(() => {
      setElapsedMinutes((Date.now() - lastFetchedAt) / 60000)
    }, 30_000)

    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [lastFetchedAt])

  const filteredItems = (data?.items ?? []).filter(
    (item) => sourceFilter === 'all' || item.source === sourceFilter,
  )

  const columns: ColumnDef<TodoItem>[] = [
    {
      id: 'priority',
      header: 'Priorität',
      accessorKey: 'priority',
      cell: ({ getValue }) => <PriorityBadge priority={getValue() as TodoPriority} />,
      size: 110,
    },
    {
      id: 'source',
      header: 'Quelle',
      accessorKey: 'source',
      cell: ({ getValue }) => (
        <StatusBadge status={getValue() as string} label={SOURCE_LABELS[getValue() as TodoSource]} />
      ),
      size: 130,
    },
    {
      id: 'title',
      header: 'Aufgabe',
      accessorKey: 'title',
      cell: ({ row }) => {
        const item = row.original
        const isExpanded = expandedTicketId === item.id
        return (
          <div>
            <div className="font-medium text-sm">{item.title}</div>
            <div className="text-xs text-muted-foreground">{item.subtitle}</div>
            {item.source === 'tanss' && isExpanded && item.meta.tanssId && (
              <TicketCommentSuggestion
                tanssId={item.meta.tanssId}
                onClose={() => setExpandedTicketId(null)}
              />
            )}
          </div>
        )
      },
    },
    {
      id: 'dueDate',
      header: 'Fällig',
      accessorKey: 'dueDate',
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        if (!v) return <span className="text-muted-foreground text-sm">—</span>
        return <span className="text-sm">{new Date(v).toLocaleDateString('de-DE')}</span>
      },
      size: 110,
    },
    {
      id: 'ageInDays',
      header: 'Alter',
      accessorKey: 'ageInDays',
      cell: ({ getValue }) => {
        const days = getValue() as number
        return <span className="text-sm text-muted-foreground">{days === 0 ? 'heute' : `${days}d`}</span>
      },
      size: 80,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <TodoAction
          item={row.original}
          expandedTicketId={expandedTicketId}
          onSapOpen={setSapModalDoc}
          onEmailDraft={setEmailDraftItem}
          onTicketToggle={(id) => setExpandedTicketId((prev) => (prev === id ? null : id))}
        />
      ),
      size: 90,
    },
  ]

  const sourceErrors: Array<{ source: string; error: string; tenantId?: string }> = []
  if (data) {
    Object.entries(data.sources).forEach(([source, status]) => {
      if (!status.ok && status.error) {
        sourceErrors.push({ source, error: status.error })
      }
      if (source === 'sap' && 'perTenantErrors' in status && status.perTenantErrors) {
        Object.entries(status.perTenantErrors).forEach(([tenantId, error]) => {
          sourceErrors.push({ source: 'sap', error: error as string, tenantId })
        })
      }
    })
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mein Tag"
        actions={
          <div className="flex items-center gap-3">
            {lastFetchedAt && (
              <span className="text-xs text-muted-foreground">
                Zuletzt aktualisiert: {formatRelativeTime(elapsedMinutes)}
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['todos-aggregated', activeTenantIds] })}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        }
      />

      {sourceErrors.length > 0 && (
        <details className="rounded-md border border-warning bg-warning/10 px-4 py-2 text-sm">
          <summary className="cursor-pointer font-medium text-warning-foreground">
            {sourceErrors.length} Fehler beim Laden
          </summary>
          <ul className="mt-2 list-disc pl-4 text-muted-foreground space-y-1">
            {sourceErrors.map((e, idx) => (
              <li key={`${e.source}-${e.tenantId || idx}`}>
                {e.tenantId ? `SAP Mandant ${e.tenantId}` : e.source}: {e.error}
              </li>
            ))}
          </ul>
        </details>
      )}

      <FilterBar>
        {(['all', 'tanss', 'sap-order', 'sap-quote', 'email'] as SourceFilter[]).map((f) => {
          const count = f === 'all'
            ? (data?.items ?? []).length
            : (data?.items ?? []).filter((i) => i.source === f).length
          return (
            <FilterChip
              key={f}
              active={sourceFilter === f}
              count={count}
              onRemove={f !== 'all' && sourceFilter === f ? () => setSourceFilter('all') : undefined}
              onClick={() => setSourceFilter(f)}
            >
              {f === 'all' ? 'Alle' : SOURCE_LABELS[f as TodoSource]}
            </FilterChip>
          )
        })}
      </FilterBar>

      {isLoading && <LoadingState label="Aufgaben werden geladen…" />}
      {isError && (
        <ErrorState
          title="Fehler beim Laden"
          description={error instanceof Error ? error.message : 'Unbekannter Fehler'}
        />
      )}
      {!isLoading && !isError && filteredItems.length === 0 && (
        <EmptyState
          icon={<FileText className="w-10 h-10 text-muted-foreground" />}
          title="Alles erledigt"
          description="Keine offenen Aufgaben gefunden."
        />
      )}
      {!isLoading && !isError && filteredItems.length > 0 && (
        <DataTable columns={columns} data={filteredItems} />
      )}

      {sapModalDoc && (
        <DocumentDetailModal
          doc={sapModalDoc}
          open={!!sapModalDoc}
          onOpenChange={(open) => { if (!open) setSapModalDoc(null) }}
          onConvert={() => {}}
        />
      )}

      {emailDraftItem && (
        <EmailDraftModal
          item={emailDraftItem}
          onClose={() => setEmailDraftItem(null)}
        />
      )}
    </div>
  )
}

// ─── Action buttons per row ──────────────────────────────────────────────────

interface TodoActionProps {
  item: TodoItem
  expandedTicketId: string | null
  onSapOpen: (doc: CachedDocument) => void
  onEmailDraft: (item: TodoItem) => void
  onTicketToggle: (id: string) => void
}

function TodoAction({ item, expandedTicketId, onSapOpen, onEmailDraft, onTicketToggle }: TodoActionProps) {
  if (item.source === 'tanss' && item.meta.tanssId) {
    const isExpanded = expandedTicketId === item.id
    return (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="xs"
          aria-label="TANSS Ticket öffnen"
          disabled
          title="TANSS Deep-Link nicht verfügbar"
        >
          <Ticket className="w-4 h-4" />
        </Button>
        <Button
          variant={isExpanded ? 'secondary' : 'ghost'}
          size="xs"
          aria-label="KI-Kommentarvorschlag anzeigen"
          title="KI-Kommentarvorschlag"
          onClick={() => onTicketToggle(item.id)}
        >
          {isExpanded
            ? <ChevronUp className="w-4 h-4" />
            : <Sparkles className="w-4 h-4" />}
        </Button>
      </div>
    )
  }

  if (item.source === 'sap-order' || item.source === 'sap-quote') {
    return (
      <Button
        variant="ghost"
        size="xs"
        aria-label="SAP-Dokument öffnen"
        onClick={() => onSapOpen(buildSapDocStub(item))}
      >
        <FileText className="w-4 h-4" />
      </Button>
    )
  }

  if (item.source === 'email') {
    return (
      <div className="flex gap-1">
        {item.deepLink && (
          <a href={item.deepLink} target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="xs" aria-label="In Outlook öffnen">
              <Mail className="w-4 h-4" />
              <ExternalLink className="w-3 h-3 ml-0.5 opacity-60" />
            </Button>
          </a>
        )}
        {item.meta.emailMessageId && (
          <Button
            variant="ghost"
            size="xs"
            aria-label="Antwort entwerfen"
            title="Antwort mit KI entwerfen"
            onClick={() => onEmailDraft(item)}
          >
            <Sparkles className="w-4 h-4" />
          </Button>
        )}
      </div>
    )
  }

  return null
}
